import { getAvailabilityLabel, ProjectDecisionWord, ProjectRoomAvailability, ProjectRoomKey, ProjectTrackMoveKind } from 'models/projects/projectEnums';
import { TFunction } from 'i18next';
import { IAppContext } from 'App/AppContext';
import { IRawStyle, keyframes, mergeStyles } from '@fluentui/react';
import { ProjectPalette } from 'components/Project/projectStyles';
import Project, {
  ProjectMilestone,
  ProjectRoomProgress,
  ProjectStep,
  ProjectSummary,
  ProjectTrack,
  ProjectTrackMove,
} from 'models/projects/project';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getProjectMilestoneRoute, getProjectRoomRoute, getProjectRoute, getProjectStepRoute, getProjectStoreContentRoute } from 'components/Project/projectRoutes';
import {
  getNextMoveGain,
  getNextMoveTodoLine,
  getRoomMoveTitle,
  getStepMoveButton,
  getStepMoveTitle,
} from 'components/Project/projectNextMoveText';

/** PackageTenantStates.Available on the API contract. */
const availablePackageState: number = 0;

interface ProjectPackageStateFacts {
  packageId?: number;
  milestones: readonly unknown[];
}

interface PackageStateFacts {
  packageState?: number;
}

/** True when the package was uninstalled but the project shell remains (no milestones). */
export const isRemovedPackageProject = (
  project: ProjectPackageStateFacts,
  tier: PackageStateFacts | undefined,
): boolean =>
  project.packageId !== undefined &&
  project.milestones.length === 0 &&
  tier?.packageState === availablePackageState;

export interface ProjectStandardRef {
  isoNormId: number;
  normName: string;
}

/**
 * First cached project norm with `isoNormId` (same lookup as project header chips). Undefined when
 * none match or cache is incomplete. No default norm fallback.
 */
export const resolveProjectStandard = (
  appContext: IAppContext,
  normIds: number[] | undefined,
): ProjectStandardRef | undefined => {
  const norms = appContext.globalDataCache.norms;

  for (const normId of normIds ?? []) {
    if (!norms.has(normId)) continue;

    const norm = norms.get(normId);
    if (norm.isoNormId !== undefined && norm.isoNormId !== null) {
      // Uses norm name when present; otherwise `ISO {isoNormId}`.
      const name: string = norm.name === undefined || norm.name === null ? '' : norm.name.trim();

      return { isoNormId: norm.isoNormId, normName: name.length > 0 ? name : `ISO ${norm.isoNormId}` };
    }
  }

  return undefined;
};

/** Gap pulse duration in ms (shared with cleanup timeout). */
const projectGapPulseDurationMs: number = 400;

/** One-shot brand outline pulse (module-scoped keyframes). */
const projectGapPulseKeyframe = keyframes({
  '0%': { outlineWidth: '0px', outlineOffset: '0px' },
  '35%': { outlineWidth: '3px', outlineOffset: '3px' },
  '100%': { outlineWidth: '0px', outlineOffset: '0px' },
});

const getProjectGapPulseStyles = (pal: ProjectPalette): IRawStyle => {
  return {
    outlineStyle: 'solid',
    outlineColor: pal.brand,
    outlineWidth: '0px',
    outlineOffset: '0px',
    animationName: projectGapPulseKeyframe,
    animationDuration: `${projectGapPulseDurationMs}ms`,
    animationTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    animationIterationCount: '1',
    selectors: {
      '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
    },
  };
};

/** Options for `focusProjectGap`. */
interface IProjectFocusGapOptions {
  /** Mark the gap with the 400ms brand pulse. Requires `palette`. */
  pulse?: boolean;

  /** The caller's palette (`projectPalette(theme, appContext.useDarkMode)`). */
  palette?: ProjectPalette;
}

/** Extra ms on pulse cleanup timer when `animationend` does not fire. */
const projectGapPulseTimeoutSlackMs: number = 200;

/** Applies pulse class; removes on `animationend` or timeout. */
const pulseElement = (element: HTMLElement, palette: ProjectPalette): void => {
  const className = mergeStyles(getProjectGapPulseStyles(palette));
  let timer: number | undefined = undefined;

  const stop = () => {
    element.classList.remove(className);
    element.removeEventListener('animationend', stop);
    if (timer !== undefined) window.clearTimeout(timer);
  };

  element.classList.add(className);
  element.addEventListener('animationend', stop);
  timer = window.setTimeout(stop, projectGapPulseDurationMs + projectGapPulseTimeoutSlackMs);
};

/** Scrolls to, focuses, and optionally pulses the element id. Missing elements are ignored. */
export const focusProjectGap = (elementId: string, opts?: IProjectFocusGapOptions): void => {
  if (typeof document === 'undefined') return;

  const element = document.getElementById(elementId);
  if (element === null) return;

  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // Non-focusable targets get tabindex -1 for programmatic focus only (not in tab order).
  if (!element.hasAttribute('tabindex') && element.tabIndex < 0) {
    element.setAttribute('tabindex', '-1');
  }

  element.focus({ preventScroll: true });

  if (opts?.pulse === true && opts.palette !== undefined) {
    pulseElement(element, opts.palette);
  }
};

/** DOM ids for `focusProjectGap` from citations or validation messages. */
export const projectTimelineAnchorId: string = 'project-timeline';

export const projectNextMoveAnchorId: string = 'project-next-move';

/** Room facts from `project.rooms` for shell UI. */
export interface IProjectRoomFacts {
  name?: string;

  /** Room key (route segment). */
  roomKey: ProjectRoomKey;

  /** Room kind from server registry. */
  kind: number;

  availability: ProjectRoomAvailability;

  /** The decision routes this room dispatches, as the server declared them. */
  actions: ProjectDecisionWord[];

  /** Server flag: decision may include `editedDescription`. */
  supportsEditDescription: boolean;

  /** Milestone task id for rail grouping (`getRoomsByMilestone`). */
  milestoneTaskId?: number;

  sortOrder: number;

  /** The room's anchor task is completed. */
  confirmed: boolean;

  /** Confirmation timestamp when sent. */
  confirmedDate?: Date;

  /** False for a room that is done when its data says so; it never offers a confirm. */
  confirmable: boolean;

  /** The room's progress from its own data, when it measures one. */
  progress?: ProjectRoomProgress;

  /** Only the confirm is left, as the server decides it. */
  readyToConfirm: boolean;

  /** The room is done, as the server decides it from stored facts. */
  done: boolean;

  /** Work in the room is recorded, as the server decides it from stored facts. */
  started: boolean;
}

/** Whether this room opens, and where it stands. */
interface IProjectRoomAvailabilityAnswer {
  availability: ProjectRoomAvailability;

  confirmed: boolean;

  confirmedDate?: Date;

  /** False for a room that is done when its data says so; it never offers a confirm. */
  confirmable: boolean;

  /** The room's progress from its own data, when it measures one. */
  progress?: ProjectRoomProgress;

  /** Only the confirm is left, as the server decides it. */
  readyToConfirm: boolean;

  /** The room is done, as the server decides it from stored facts. */
  done: boolean;

  /** Work in the room is recorded, as the server decides it from stored facts. */
  started: boolean;
}

/** Default availability when the room key is missing or unknown. */
const projectRoomUnknown: IProjectRoomAvailabilityAnswer = Object.freeze({
  availability: 'notSeeded',
  confirmed: false,
  confirmable: true,
  readyToConfirm: false,
  done: false,
  started: false,
});

/** Rooms from server rows in sort order; rows without `roomKey` are omitted. */
export const getProjectRoomFacts = (project: Project): IProjectRoomFacts[] => {
  const facts: IProjectRoomFacts[] = [];

  project.rooms.forEach((room) => {
    if (room.roomKey.length === 0) return;

    facts.push({
      name: room.name,
      roomKey: room.roomKey,
      kind: room.kind,
      availability: room.availability,
      actions: room.actions,
      supportsEditDescription: room.supportsEditDescription,
      milestoneTaskId: room.milestoneTaskId,
      sortOrder: room.sortOrder,
      confirmed: room.confirmed,
      confirmedDate: room.confirmedDate,
      confirmable: room.confirmable,
      progress: room.progress,
      readyToConfirm: room.readyToConfirm,
      done: room.done,
      started: room.started,
    });
  });

  return facts.sort((a, b) => a.sortOrder - b.sortOrder || a.roomKey.localeCompare(b.roomKey));
};

/** Room availability for a key. Pure (usable outside React). */
export const getRoomAvailability = (project: Project, roomKey: string | undefined): IProjectRoomAvailabilityAnswer => {
  if (roomKey === undefined || roomKey.length === 0) return projectRoomUnknown;

  const facts = getProjectRoomFacts(project).find((room) => room.roomKey === roomKey);
  if (facts === undefined) return projectRoomUnknown;

  return {
    availability: facts.availability,
    confirmed: facts.confirmed,
    confirmedDate: facts.confirmedDate,
    confirmable: facts.confirmable,
    progress: facts.progress,
    readyToConfirm: facts.readyToConfirm,
    done: facts.done,
    started: facts.started,
  };
};

// Room status: `confirmed` plus availability. Translation keys are string literals so the scanner finds them.
// Requires detail-loaded project (`hasDetail === false` means rooms not loaded).

/** Status tone. `behind` is project-level only; `getRoomStatus` does not return it. */
type StatusTone = 'done' | 'ready' | 'todo' | 'attention' | 'behind';

/** One room's status: the label, its tone, and the supporting fact when there is one. */
export interface ProjectRoomStatus {
  tone: StatusTone;

  /** The status label, already translated. */
  label: string;

  /** The single supporting sentence under the label (the availability fact, what is left to confirm). */
  metaText?: string;
}

/**
 * Room status from the server's `done`, then availability: done; notSeeded (+ meta). A live room the server reads as
 * `readyToConfirm` -> ready to confirm (+ meta when it measures nothing); a live room with `progress` -> "x of y";
 * otherwise todo. Unknown key -> todo. Callers need `hasDetail === true` before trusting room rows.
 */
export const getRoomStatus = (
  project: Project,
  roomKey: string | undefined,
  t: TFunction<string[]>,
): ProjectRoomStatus => {
  if (roomKey === undefined || roomKey.length === 0) return { tone: 'todo', label: t('projects:Status.ToDo') };

  const answer = getRoomAvailability(project, roomKey);
  if (answer.done) {
    return { tone: 'done', label: t('projects:Status.Done') };
  }

  switch (answer.availability) {
    case 'notSeeded':
      return {
        tone: 'todo',
        label: t('projects:Status.ToDo'),
        metaText: getAvailabilityLabel(t, answer.availability),
      };
    case 'live':
      if (answer.readyToConfirm) {
        // A room that measures nothing (Rollen when the package installed no role) says what confirming means.
        return answer.progress === undefined || answer.progress.total === 0
          ? { tone: 'ready', label: t('projects:Status.ReadyToConfirm'), metaText: t('projects:Status.ReadyMeta') }
          : { tone: 'ready', label: t('projects:Status.ReadyToConfirm') };
      }
      if (answer.progress !== undefined && answer.progress.total > 0) {
        return {
          tone: 'todo',
          label: t('projects:Status.Progress', { done: answer.progress.done, total: answer.progress.total }),
        };
      }

      return { tone: 'todo', label: t('projects:Status.ToDo') };
    default:
      return { tone: 'todo', label: t('projects:Status.ToDo') };
  }
};

// Rail model (pure, no React): milestones from `project.milestones`, rooms via `getProjectRoomFacts`.
// Milestone done/total counts are the server's room and step counts.

/**
 * Rail row state. `inProgress` reflects unconfirmed draft changes (`changedRoomKeys`) or recorded progress on a
 * room that is not done; glyph only, the counts are the server's `roomsDone`.
 */
export type ProjectNavRowState = 'done' | 'current' | 'open' | 'inProgress' | 'unavailable';

/** Shared rail row fields. `rowKey` is the stable row id (room, step, or milestone). */
interface ProjectNavRowBase {
  rowKey: string;

  /** Display label from server data. */
  label: string;

  state: ProjectNavRowState;

  /** Room `confirmed` or step `completed`; milestone counts use this, not `state`. */
  confirmed: boolean;

  availability: ProjectRoomAvailability;

  /** Navigation target for the row. */
  route: string;
}

/** One room, as a rail row. */
export interface ProjectNavRoomRow extends ProjectNavRowBase {
  role: 'anchor';

  roomKey: ProjectRoomKey;
}

/** One step, as a rail row: the deliverable is the step, a platform Task. */
interface ProjectNavStepRow extends ProjectNavRowBase {
  role: 'step';

  /** The milestone this step belongs to. */
  milestoneTaskId: number;

  /** The step's own platform task - its identity everywhere. */
  taskId: number;
}

/** Room (`anchor`) or step deliverable row; `role` matches server `ProjectTask.Role`. */
export type ProjectNavDeliverable = ProjectNavRoomRow | ProjectNavStepRow;

/** A room row's identity: `room:{roomKey}`. */
export const getProjectRoomRowKey = (roomKey: string): string => `room:${roomKey}`;

/** A step row's identity: `step:{taskId}`. */
export const getProjectStepRowKey = (taskId: number): string => `step:${taskId}`;

/** One milestone, as a rail row. `doneCount`/`totalCount` are the server's room plus step counts. */
export interface ProjectNavMilestone {
  /** The milestone's identity - its own platform task. */
  taskId: number;

  /** The milestone task's own name. */
  label: string;

  /** Milestone description text. */
  description: string;

  state: ProjectNavRowState;

  deliverables: ProjectNavDeliverable[];

  doneCount: number;

  totalCount: number;
}

/** Rooms by `milestoneTaskId` (rooms without milestone are omitted). */
const getRoomsByMilestone = (project: Project): Map<number, IProjectRoomFacts[]> => {
  const rooms = new Map<number, IProjectRoomFacts[]>();

  getProjectRoomFacts(project).forEach((room) => {
    // Skip rooms with no `milestoneTaskId`.
    if (room.milestoneTaskId === undefined) return;

    const group = rooms.get(room.milestoneTaskId);
    if (group === undefined) {
      rooms.set(room.milestoneTaskId, [room]);
    } else {
      group.push(room);
    }
  });

  return rooms;
};

/**
 * Deliverable row state order: unavailable, inProgress (`changedRoomKeys`), done (the server's `done`),
 * inProgress (the server's `started`, not done), current (selected row), open. `current` is after `inProgress` so a
 * started room keeps the in-progress glyph.
 */
const getDeliverableState = (
  room: IProjectRoomFacts,
  selectedRowKey: string | undefined,
  changedRoomKeys: ReadonlySet<string>,
): ProjectNavRowState => {
  if (room.availability === 'notSeeded') return 'unavailable';
  if (changedRoomKeys.has(room.roomKey)) return 'inProgress';
  if (room.done) return 'done';
  // Work recorded but the room not done yet (a role named, an inquiry sent): the clock, so the rail tells a started
  // room from one with no recorded work.
  if (room.started) return 'inProgress';
  if (getProjectRoomRowKey(room.roomKey) === selectedRowKey) return 'current';

  return 'open';
};

/**
 * Milestone row state: inProgress (pending edits), done (`milestone.completed`), inProgress (a confirmed deliverable,
 * or the server's milestone state: something under it started), current (selected milestone/row, or on the overview
 * the milestone of the track's primary move), open.
 */
const getMilestoneState = (
  milestone: ProjectMilestone,
  deliverables: ProjectNavDeliverable[],
  resumeMilestoneTaskId?: number,
  selectedRowKey?: string,
  selectedMilestoneTaskId?: number,
): ProjectNavRowState => {
  const holds = (rowKey: string | undefined): boolean =>
    rowKey !== undefined && deliverables.some((deliverable) => deliverable.rowKey === rowKey);

  // Current milestone: selected milestone page, selected deliverable row, or overview resume milestone.
  const isHere: boolean =
    (selectedMilestoneTaskId !== undefined && milestone.taskId === selectedMilestoneTaskId) ||
    holds(selectedRowKey) ||
    (resumeMilestoneTaskId !== undefined && milestone.taskId === resumeMilestoneTaskId);

  if (deliverables.some((deliverable) => deliverable.state === 'inProgress')) return 'inProgress';
  if (milestone.completed) return 'done';
  if (milestone.state === 'inProgress' || deliverables.some((deliverable) => deliverable.confirmed)) return 'inProgress';
  if (isHere) return 'current';

  return 'open';
};

/** Milestone deliverables: anchor rooms then steps (each sorted by `sortOrder`). */
const getMilestoneDeliverables = (
  project: Project,
  t: TFunction<string[]>,
  milestone: ProjectMilestone,
  rooms: IProjectRoomFacts[],
  selectedRowKey: string | undefined,
  changedRoomKeys: ReadonlySet<string>,
): ProjectNavDeliverable[] => {
  const roomRows: ProjectNavDeliverable[] = rooms.map((room) => {
    const state = getDeliverableState(room, selectedRowKey, changedRoomKeys);
    const row: ProjectNavRoomRow = {
      rowKey: getProjectRoomRowKey(room.roomKey),
      role: 'anchor',
      roomKey: room.roomKey,
      label: room.name ?? t('projects:General.NotAvailable'),
      state: state,
      confirmed: room.confirmed,
      availability: room.availability,
      route: getProjectRoomRoute(project.projectId, room.roomKey),
    };

    return row;
  });

  const stepRows: ProjectNavDeliverable[] = [...milestone.tasks]
    .sort((left, right) => left.sortOrder - right.sortOrder || left.taskId - right.taskId)
    .map((task) => {
      const rowKey = getProjectStepRowKey(task.taskId);
      // A started step (not completed, an item ticked or its task under way) keeps the clock while selected, as a room.
      const state: ProjectNavRowState = task.completed
        ? 'done'
        : task.started
        ? 'inProgress'
        : rowKey === selectedRowKey
        ? 'current'
        : 'open';
      const row: ProjectNavStepRow = {
        rowKey: rowKey,
        role: 'step',
        milestoneTaskId: milestone.taskId,
        taskId: task.taskId,
        label: task.name,
        state: state,
        confirmed: task.completed,
        // Step rows use availability `live` (never unavailable).
        availability: 'live',
        route: getProjectStepRoute(project.projectId, task.taskId),
      };

      return row;
    });

  return [...roomRows, ...stepRows];
};

/**
 * Builds the project rail. Returns `[]` when `hasDetail === false`. `changedRoomKeys` drives
 * `inProgress`; on the overview the track's primary move marks the current milestone.
 */
export const buildProjectNavModel = (
  project: Project,
  t: TFunction<string[]>,
  selectedRowKey?: string,
  selectedMilestoneTaskId?: number,
  changedRoomKeys: ReadonlySet<string> = new Set<string>(),
): ProjectNavMilestone[] => {
  if (!project.hasDetail) return [];

  const roomsByMilestone = getRoomsByMilestone(project);

  // Applies on the overview only (no selected row or milestone).
  const resumeMilestoneTaskId: number | undefined =
    selectedRowKey === undefined && selectedMilestoneTaskId === undefined
      ? project.track?.getPrimaryMove()?.milestoneTaskId
      : undefined;

  return project.milestones.map((milestone) => {
    const deliverables: ProjectNavDeliverable[] = getMilestoneDeliverables(
      project,
      t,
      milestone,
      roomsByMilestone.get(milestone.taskId) ?? [],
      selectedRowKey,
      changedRoomKeys,
    );

    return {
      taskId: milestone.taskId,
      label: milestone.name,
      description: milestone.description,
      state: getMilestoneState(milestone, deliverables, resumeMilestoneTaskId, selectedRowKey, selectedMilestoneTaskId),
      deliverables: deliverables,
      doneCount: milestone.roomsDone + milestone.tasksDone,
      totalCount: milestone.roomsTotal + milestone.tasksTotal,
    };
  });
};

/** Done rooms plus completed steps over the whole project, from the server's milestone counts. */
export const getProjectDeliverableCounts = (project: Project): { done: number; total: number } => {
  return project.milestones.reduce(
    (totals, milestone) => ({
      done: totals.done + milestone.roomsDone + milestone.tasksDone,
      total: totals.total + milestone.roomsTotal + milestone.tasksTotal,
    }),
    { done: 0, total: 0 },
  );
};

// Primary forward CTA when the track names no move: routing on server facts only. List project
// (`hasDetail === false`) routes to the overview. Translation keys are string literals so the
// scanner finds them.

/** Forward action kind (for optional UI emphasis). */
type ProjectNextActionKind = 'create-project' | 'open-project' | 'journey-empty' | 'project-complete';

/** Resolved forward action for rendering. */
interface ProjectNextAction {
  kind: ProjectNextActionKind;

  /** Translated button label. */
  label: string;

  /** Optional translated explanation line. */
  explanation?: string;

  /** Where clicking goes. */
  route?: string;
}

/** Input for `getProjectNextAction`. */
interface IProjectNextActionInput {
  /** Project context; a list summary or undefined on list/store/widget surfaces. */
  project?: Project | ProjectSummary;

  /** Rail from `buildProjectNavModel`; empty when `hasDetail === false`. */
  milestones: ProjectNavMilestone[];
}

/**
 * Resolves the primary forward action: no project -> create; no detail -> overview; no milestones ->
 * journey-empty; completed -> project-complete; else the overview.
 */
export const getProjectNextAction = (t: TFunction<string[]>, input: IProjectNextActionInput): ProjectNextAction => {
  const project = input.project;
  if (project === undefined) {
    return {
      kind: 'create-project',
      label: t('projects:Actions.InstallFromStore'),
      route: getProjectStoreContentRoute(),
    };
  }

  const overviewRoute = getProjectRoute(project.projectId);

  if (!(project instanceof Project) || !project.hasDetail) {
    return {
      kind: 'open-project',
      label: t('projects:Cta.BackToProject'),
      route: overviewRoute,
    };
  }

  // Distinguish empty journey (no milestones) from complete (requires `hasDetail` above).
  if (input.milestones.length === 0) {
    return {
      kind: 'journey-empty',
      label: t('projects:Cta.BackToProject'),
      explanation: t('projects:Band.JourneyEmpty'),
      route: overviewRoute,
    };
  }

  if (project.isCompleted) {
    return {
      kind: 'project-complete',
      label: t('projects:Cta.ProjectDone'),
      explanation: t('projects:Band.ProjectComplete'),
      // Project complete: link to overview.
      route: overviewRoute,
    };
  }

  return {
    kind: 'open-project',
    label: t('projects:Cta.BackToProject'),
    route: overviewRoute,
  };
};

/**
 * Replaces commit-action label with ask-manager copy for non-managers. Routing unchanged.
 */
export const getProjectManagerLabel = (t: TFunction<string[]>, appContext: IAppContext, label: string): string => {
  return getProjectManagerLabelFor(t, isProjectManager(appContext), label);
};

/** True when the user has `FeatureTypes.GenericManager`. */
export const isProjectManager = (appContext: IAppContext): boolean => {
  return hasUserFeature(appContext, FeatureTypes.GenericManager);
};

/** Label swap given an already-resolved manager flag. */
const getProjectManagerLabelFor = (t: TFunction<string[]>, isManager: boolean, label: string): string => {
  if (isManager) return label;

  return t('projects:Actions.AskManager');
};

// Next move: the server track's primary move, with the `getProjectNextAction` fallback (no track,
// no moves, or `hasDetail === false`). A target-date move leads only when the server puts it first.

/** Move kind: client action kinds plus `ProjectTrackMoveKind`. */
type ProjectNextMoveKind = ProjectNextActionKind | ProjectTrackMoveKind;

/** Non-route move: opens target-date dialog. */
export type ProjectNextMoveAction = 'reviewTarget' | 'setTarget';

/** The primary move: what to do, what is open, what it gains, and where the button goes. */
interface IProjectNextMovePrimary {
  kind: ProjectNextMoveKind;

  /** The action: "Volg de uitvragen op". */
  title: string;

  /** One line under the title: what is still open in the room, or why this step; undefined with nothing to say. */
  detail?: string;

  /** The gain as a sentence; undefined when the move gains nothing or it is not known yet. */
  gain?: string;

  /** The button: where it goes ("Naar Voortgang verantwoordelijken"), or what it does. */
  buttonText: string;

  /** Where clicking goes. */
  route?: string;

  /** What clicking does, when it is not a navigation. */
  action?: ProjectNextMoveAction;
}

/** Next-move result. The primary has a `route` or an `action`. */
export interface IProjectNextMove {
  primary: IProjectNextMovePrimary;
}

/** Input for `getProjectNextMove`. */
interface IProjectNextMoveInput {
  /** Project context; a list summary until the detail read is in, undefined when no project is loaded. */
  project?: Project | ProjectSummary;

  /** The rail model as the caller already built it. */
  milestones: ProjectNavMilestone[];

  /** Whether the current user may confirm a commit-kind move. Affects copy only, not routing. */
  isManager: boolean;
}

/** Resolves milestone for a track move. */
const getMilestone = (project: Project, milestoneTaskId: number | undefined): ProjectMilestone | undefined => {
  if (milestoneTaskId === undefined) return undefined;

  return project.milestones.find((milestone) => milestone.taskId === milestoneTaskId);
};

/** Resolves step for a `completeStageTask` move. */
const getStep = (milestone: ProjectMilestone | undefined, taskId: number | undefined): ProjectStep | undefined => {
  if (milestone === undefined || taskId === undefined) return undefined;

  return milestone.tasks.find((task) => task.taskId === taskId);
};

/** One move in words: its action as a title, its button, the line under it, and where it goes. */
interface IProjectMoveWords {
  title: string;

  buttonText: string;

  detail?: string;

  route?: string;

  action?: ProjectNextMoveAction;
}

/** Why a step: where it stands in its milestone. */
const getStepWhy = (t: TFunction<string[]>, milestone: ProjectMilestone): string => {
  const left: number = Math.max(0, milestone.tasksTotal - milestone.tasksDone - 1);

  if (left === 0) return t('projects:NextMove.CompleteTaskWhyLast', { stage: milestone.name });
  if (left === 1) return t('projects:NextMove.CompleteTaskWhyOne', { stage: milestone.name });

  return t('projects:NextMove.CompleteTaskWhy', { stage: milestone.name, left: left });
};

/** Whether only the confirm is left in a room: nothing counted open, and the server reads it as ready to confirm. */
const isRoomReadyToConfirm = (project: Project, move: ProjectTrackMove): boolean =>
  move.todo.length === 0 && getRoomAvailability(project, move.roomKey).readyToConfirm;

/** A server track move in words; undefined for an unknown kind or a missing milestone, step or room. */
const getMoveWords = (
  t: TFunction<string[]>,
  project: Project,
  move: ProjectTrackMove,
): IProjectMoveWords | undefined => {
  const milestone: ProjectMilestone | undefined = getMilestone(project, move.milestoneTaskId);

  switch (move.kind) {
    case 'completeStageTask': {
      const step: ProjectStep | undefined = getStep(milestone, move.taskId);
      if (milestone === undefined || step === undefined) return undefined;

      return {
        title: getStepMoveTitle(t, step),
        buttonText: getStepMoveButton(t, step),
        detail: getStepWhy(t, milestone),
        route: getProjectStepRoute(project.projectId, step.taskId),
      };
    }
    case 'markStageDone':
      if (milestone === undefined) return undefined;

      return {
        title: t('projects:NextMove.Title.MarkStageDone', { stage: milestone.name }),
        buttonText: t('projects:NextMove.GoTo', { name: milestone.name }),
        detail: t('projects:NextMove.MarkStageDoneWhy', { stage: milestone.name }),
        route: getProjectMilestoneRoute(project.projectId, milestone.taskId),
      };
    case 'openRoom': {
      // Drop move when `roomKey` is not on the project.
      const roomKey: string | undefined = move.roomKey;
      if (roomKey === undefined || !project.rooms.some((room) => room.roomKey === roomKey)) return undefined;

      const name: string = project.getRoomName(roomKey);

      // With only the confirm left, the action is the confirm.
      if (isRoomReadyToConfirm(project, move)) {
        return {
          title: t('projects:NextMove.Title.Confirm', { name: name }),
          buttonText: t('projects:NextMove.GoTo', { name: name }),
          detail: t('projects:NextMove.ReadyToConfirm'),
          route: getProjectRoomRoute(project.projectId, roomKey),
        };
      }

      return {
        title: getRoomMoveTitle(t, roomKey, name),
        buttonText: t('projects:NextMove.GoTo', { name: name }),
        detail: getNextMoveTodoLine(t, move.todo),
        route: getProjectRoomRoute(project.projectId, roomKey),
      };
    }
    case 'setUpStageTasks':
      if (milestone === undefined) return undefined;

      return {
        title: t('projects:NextMove.Title.SetUpStageTasks', { stage: milestone.name }),
        buttonText: t('projects:NextMove.GoTo', { name: milestone.name }),
        route: getProjectMilestoneRoute(project.projectId, milestone.taskId),
      };
    case 'reviewTarget': {
      const label: string = t('projects:NextMove.Link.ReviewTarget');

      return { title: label, buttonText: label, action: 'reviewTarget' };
    }
    case 'setTarget':
      return {
        title: t('projects:NextMove.Title.SetTarget'),
        buttonText: t('projects:NextMove.Link.SetTarget'),
        detail: t('projects:NextMove.SetTargetWhy'),
        action: 'setTarget',
      };
    default:
      return undefined;
  }
};

/**
 * Applies the ask-manager swap to the button of a move that opens the target date dialog. The other buttons only
 * navigate.
 */
const getPrimaryButtonText = (
  t: TFunction<string[]>,
  isManager: boolean,
  kind: ProjectNextMoveKind,
  buttonText: string,
): string => {
  switch (kind) {
    case 'reviewTarget':
    case 'setTarget':
      return getProjectManagerLabelFor(t, isManager, buttonText);
    default:
      return buttonText;
  }
};

/** Converts `ProjectNextAction` to the primary move: its label is the title and the button. */
const fromNextAction = (action: ProjectNextAction): IProjectNextMovePrimary => {
  return {
    kind: action.kind,
    title: action.label,
    detail: action.explanation,
    buttonText: action.label,
    route: action.route,
  };
};

/** Whether a move is about the target date. */
const isTargetMove = (move: ProjectTrackMove): boolean => move.kind === 'reviewTarget' || move.kind === 'setTarget';

/**
 * Resolves the primary next move: the track's primary move, else the first other move this client can word; a
 * target-date move leads only when the server puts it forward (before the start, a target date is what starts the
 * project). Falls back to `getProjectNextAction` when the track names nothing this client can word.
 */
export const getProjectNextMove = (t: TFunction<string[]>, input: IProjectNextMoveInput): IProjectNextMove => {
  const action: ProjectNextAction = getProjectNextAction(t, {
    project: input.project,
    milestones: input.milestones,
  });

  const project: Project | undefined = input.project instanceof Project ? input.project : undefined;
  const track: ProjectTrack | undefined = project?.track;
  const primaryMove: ProjectTrackMove | undefined = track?.getPrimaryMove();

  // Fallback to client resolver when track cannot supply primary.
  if (
    project === undefined ||
    project.hasDetail !== true ||
    track === undefined ||
    track.moves.length === 0 ||
    primaryMove === undefined
  ) {
    return { primary: fromNextAction(action) };
  }

  const candidates: ProjectTrackMove[] = [
    primaryMove,
    ...track.moves.filter((move) => move !== primaryMove && !isTargetMove(move)),
  ];
  const lead = candidates
    .map((move) => ({ move: move, words: getMoveWords(t, project, move) }))
    .find(
      (candidate): candidate is { move: ProjectTrackMove; words: IProjectMoveWords } => candidate.words !== undefined,
    );

  if (lead === undefined) return { primary: fromNextAction(action) };

  const primary: IProjectNextMovePrimary = {
    kind: lead.move.kind,
    title: lead.words.title,
    detail: lead.words.detail,
    gain: getNextMoveGain(t, lead.move.impactDays),
    buttonText: getPrimaryButtonText(t, input.isManager, lead.move.kind, lead.words.buttonText),
    route: lead.words.route,
    action: lead.words.action,
  };

  return { primary: primary };
};
